import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { CameraOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  /** Gọi một lần khi đọc được mã; muốn quét lại thì đổi `key` để mount lại component */
  onResult: (text: string) => void;
}

/** Khoảng cách giữa hai lần giải mã khung hình, đủ nhanh mà không ngốn CPU của kiosk */
const SCAN_INTERVAL = 150;
/** Máy quét cầm tay gõ cả chuỗi trong vài chục ms; phím cách nhau lâu hơn là người gõ tay */
const WEDGE_KEY_GAP = 100;

/**
 * Quét QR bằng camera. Dùng BarcodeDetector có sẵn của trình duyệt nếu hỗ trợ (Chrome Android,
 * macOS), còn lại giải mã bằng jsQR. Đồng thời nhận máy quét QR dạng bàn phím (gõ chuỗi + Enter).
 */
export default function QrScanner({ onResult }: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const doneRef = useRef(false);

  /** Lưu key dịch thay vì câu đã dịch, để đổi ngôn ngữ giữa chừng vẫn hiện đúng */
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [mirrored, setMirrored] = useState(false);

  const emit = (text: string) => {
    const value = text.trim();
    if (!value || doneRef.current) return;
    doneRef.current = true;
    onResultRef.current(value);
  };

  // Camera
  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer = 0;

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('checkin.scanner.no_camera');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
      } catch (e: any) {
        setError(
          e?.name === 'NotAllowedError' ? 'checkin.scanner.no_permission' : 'checkin.scanner.camera_error'
        );
        return;
      }
      if (stopped) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      // Webcam trước mặt khách thì lật ngang cho dễ canh mã như soi gương
      setMirrored(stream.getVideoTracks()[0]?.getSettings().facingMode !== 'environment');
      setReady(true);

      const Detector = (window as any).BarcodeDetector;
      let detector: any = null;
      if (Detector) {
        try {
          const formats: string[] | undefined = await Detector.getSupportedFormats?.();
          if (!formats || formats.includes('qr_code')) detector = new Detector({ formats: ['qr_code'] });
        } catch {
          detector = null;
        }
      }

      const scan = async () => {
        if (stopped || doneRef.current) return;
        if (video.readyState >= 2 && video.videoWidth) {
          let text: string | null = null;
          if (detector) {
            try {
              const codes = await detector.detect(video);
              text = codes[0]?.rawValue ?? null;
            } catch {
              detector = null; // lỗi giữa chừng thì chuyển hẳn sang jsQR
            }
          } else {
            const canvas = canvasRef.current;
            const ctx = canvas?.getContext('2d', { willReadFrequently: true });
            if (canvas && ctx) {
              // Thu nhỏ khung hình trước khi giải mã, mã QR trên điện thoại vẫn đủ rõ
              const scale = Math.min(1, 640 / video.videoWidth);
              canvas.width = Math.round(video.videoWidth * scale);
              canvas.height = Math.round(video.videoHeight * scale);
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
              text = jsQR(img.data, img.width, img.height, { inversionAttempts: 'attemptBoth' })?.data ?? null;
            }
          }
          if (text && !stopped) {
            emit(text);
            return;
          }
        }
        timer = window.setTimeout(scan, SCAN_INTERVAL);
      };
      scan();
    })();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // Máy quét QR dạng bàn phím (USB/Bluetooth)
  useEffect(() => {
    let buffer = '';
    let last = 0;
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest?.('input, textarea, select')) return;
      const now = Date.now();
      if (now - last > WEDGE_KEY_GAP) buffer = '';
      last = now;
      if (e.key === 'Enter') {
        if (buffer.length >= 2) emit(buffer);
        buffer = '';
      } else if (e.key.length === 1) {
        buffer += e.key;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      className="bg-black rounded-xl overflow-hidden mb-4 relative flex flex-col items-center justify-center border-4 border-gray-800"
      style={{ minHeight: '250px' }}
    >
      {error ? (
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <CameraOff size={40} className="text-white/40" />
          <p className="text-white/70 text-xs leading-relaxed">{t(error)}</p>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            style={mirrored ? { transform: 'scaleX(-1)' } : undefined}
          />
          <div className="w-48 h-48 border-2 border-white/70 rounded-lg relative overflow-hidden z-10 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
            <div className="absolute top-0 left-0 w-full h-1 bg-green-500 shadow-[0_0_10px_#22c55e] animate-[subtleFloat_2s_linear_infinite]"></div>
          </div>
          <p className="text-white/80 text-xs mt-4 z-10 drop-shadow">
            {ready ? t('checkin.scanner.scanning') : t('checkin.scanner.opening')}
          </p>
        </>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
